import { AddToCartButton, DeliveryPromise, MediaGallery, PaymentAssurance, ProductBottomBanner, ProductSlider, ProductSliderSkeleton, ProductTitle, ProductTopBanner, ProductVariants, RecentlyViewed, RelatedProducts, Reviews, ReviewsCount, ReviewsSkeleton, SchemaMarkup, ShareProductButton, TabbyPromoSnippet, TamaraPromoSnippet, UpsellProducts, WebEngagePageViewEvent } from "@repo/components";
import { getBreadCrumbs, getTabItems } from "@repo/constants/product";
import { getFormattedProduct, updateBreadcrumbs } from "@repo/lib/helpers";
import { generateBreadcrumbSchema, generateProductSchema, getFeatureFlag } from "@repo/lib/utils";
import { Accordion, BreadCrumb, ProductPrice } from "@repo/ui";
import { getTranslations, unstable_setRequestLocale } from "next-intl/server";
import { Suspense } from "react";
const ProductDetailsLayout = async ({
  productData,
  wishlist,
  isLoggedIn,
  locale,
  productSlug,
  categorySlug,
  spaceSlug,
  collectionName
}) => {
  unstable_setRequestLocale(locale);
  const t = await getTranslations("Common");
  const canonicalUrl = process.env.NEXT_CANONICAL_URL;
  const isWebEngageEnabled = getFeatureFlag("webEngage");
  const {
    id,
    name,
    discount,
    formattedPrice,
    formattedSalePrice,
    images = [],
    isStockStatus,
    description,
    topCategoryDesktopBanners,
    topCategoryMobileBanners,
    bottomDesktopBanners,
    bottomMobileBanners,
    categoryName,
    averageRating,
    glance,
    highlights,
    productOptions,
    parentSlug,
    sku,
    slug,
    brand,
    relatedItemsCount,
    upsellProductCount,
    ratingItemCount,
    price,
    categories,
    videos = []
  } = productData;
  const TabItems = getTabItems(description, glance, highlights, productOptions);
  const breadcrumbs = getBreadCrumbs(categoryName, name, categorySlug, productSlug, t);
  const productSchema = generateProductSchema({
    name,
    description,
    brand,
    locale,
    slug,
    images,
    sku,
    formattedPrice,
    isStockStatus,
    averageRating
  });
  const updatedBreadcrumbs = updateBreadcrumbs(breadcrumbs, spaceSlug, categorySlug, collectionName);
  const breadcrumbSchema = generateBreadcrumbSchema({
    locale,
    breadcrumbs
  });
  return <>
      {isWebEngageEnabled && <WebEngagePageViewEvent eventName="product_viewed" eventData={getFormattedProduct(productData)} />}
      <ProductTopBanner topCategoryDesktopBanners={topCategoryDesktopBanners} topCategoryMobileBanners={topCategoryMobileBanners} categoryName={categoryName} categorySlug={categorySlug} data-sentry-element="ProductTopBanner" data-sentry-source-file="ProductDetailsLayout.jsx" />
      <div className="px-4">
        <div className="mx-auto min-h-screen md:max-w-screen-xl">
          <BreadCrumb items={spaceSlug ? updatedBreadcrumbs : breadcrumbs} data-sentry-element="BreadCrumb" data-sentry-source-file="ProductDetailsLayout.jsx" />
          <div className="w-full justify-between md:flex md:space-x-5 rtl:space-x-reverse">
            <div className="relative md:w-1/2 md:px-0">
              <div className="md:sticky md:top-[85px]">
                <MediaGallery media={[...images, ...videos]} discount={discount} productId={productData?.id} items={wishlist?.content} session={isLoggedIn} product={productData} locale={locale} data-sentry-element="MediaGallery" data-sentry-source-file="ProductDetailsLayout.jsx" />
              </div>
            </div>
            <div className="pt-4 md:w-1/2 md:pt-0">
              <ProductTitle productName={name} data-sentry-element="ProductTitle" data-sentry-source-file="ProductDetailsLayout.jsx" />
              {ratingItemCount > 0 && <Suspense>
                  <ReviewsCount averageRating={averageRating} itemId={id} />
                </Suspense>}
              <div className="flex justify-between">
                <ProductPrice price={formattedPrice} salePrice={formattedSalePrice} discount={discount} variant="productDetails" data-sentry-element="ProductPrice" data-sentry-source-file="ProductDetailsLayout.jsx" />
                <ShareProductButton product={productData} categorySlug={categorySlug} locale={locale} canonicalUrl={canonicalUrl} data-sentry-element="ShareProductButton" data-sentry-source-file="ProductDetailsLayout.jsx" />
              </div>
              {sku && <div className="bg-productDetails-bg text-productDetails-text mt-4 w-max p-3 text-sm font-medium">
                  {t("SKU")} {sku}
                </div>}
              {!!productOptions?.length && <ProductVariants productOptions={productOptions} parentSlug={parentSlug} locale={locale} categorySlug={categorySlug} spaceSlug={spaceSlug} />}
              <div className="flex space-x-6 pt-6 rtl:space-x-reverse">
                <AddToCartButton product={productData} isInStock={isStockStatus} isQtySelectorEnabled={true} variant="primary" breadCrumbs={breadcrumbs} categorySlug={categorySlug} spaceSlug={spaceSlug} data-sentry-element="AddToCartButton" data-sentry-source-file="ProductDetailsLayout.jsx" />
              </div>

              <div className="bg-productDetails-textBg mt-4 items-center justify-center gap-3 p-4 sm:justify-start md:flex">
                <p className="text-productDetails-subHeadingText pb-3 text-center text-sm sm:block md:pb-0 md:text-start">
                  {t("Product_Secure_Checkout_Message")}
                </p>
                <PaymentAssurance locale={locale} data-sentry-element="PaymentAssurance" data-sentry-source-file="ProductDetailsLayout.jsx" />
              </div>
              <DeliveryPromise data-sentry-element="DeliveryPromise" data-sentry-source-file="ProductDetailsLayout.jsx" />
              <div className="bg-productDetails-textBg">
                <TabbyPromoSnippet price={formattedSalePrice} lang={locale} data-sentry-element="TabbyPromoSnippet" data-sentry-source-file="ProductDetailsLayout.jsx" />
              </div>
              <div className="bg-productDetails-textBg">
                <TamaraPromoSnippet price={formattedSalePrice} lang={locale} data-sentry-element="TamaraPromoSnippet" data-sentry-source-file="ProductDetailsLayout.jsx" />
              </div>
              <Accordion items={TabItems} staticTitle={true} data-sentry-element="Accordion" data-sentry-source-file="ProductDetailsLayout.jsx" />
            </div>
          </div>

          <>
            <Suspense fallback={<ReviewsSkeleton />} data-sentry-element="Suspense" data-sentry-source-file="ProductDetailsLayout.jsx">
              <Reviews itemId={id} session={isLoggedIn} locale={locale} data-sentry-element="Reviews" data-sentry-source-file="ProductDetailsLayout.jsx" />
            </Suspense>
            {isLoggedIn && wishlist?.content?.length > 0 && <ProductSlider title={t("Favourites")} items={wishlist?.content} wishlistItems={wishlist?.content} type="favorite" isMedium session={isLoggedIn} />}
            {relatedItemsCount > 0 && <Suspense fallback={<ProductSliderSkeleton />}>
                <RelatedProducts itemId={id} wishlistItems={wishlist?.content} session={isLoggedIn} locale={locale} />
              </Suspense>}
            {upsellProductCount > 0 && <Suspense fallback={<ProductSliderSkeleton />}>
                <UpsellProducts itemId={id} wishlistItems={wishlist?.content} session={isLoggedIn} locale={locale} />
              </Suspense>}
          </>
          <RecentlyViewed productSku={sku} session={isLoggedIn} locale={locale} wishlistItems={wishlist?.content} data-sentry-element="RecentlyViewed" data-sentry-source-file="ProductDetailsLayout.jsx" />

          <ProductBottomBanner bottomDesktopBanners={bottomDesktopBanners} bottomMobileBanners={bottomMobileBanners} productName={name} data-sentry-element="ProductBottomBanner" data-sentry-source-file="ProductDetailsLayout.jsx" />
        </div>
      </div>
      <SchemaMarkup schema={productSchema} id="product-schema" data-sentry-element="SchemaMarkup" data-sentry-source-file="ProductDetailsLayout.jsx" />
      <SchemaMarkup schema={breadcrumbSchema} id="breadcrumb-schema" data-sentry-element="SchemaMarkup" data-sentry-source-file="ProductDetailsLayout.jsx" />
    </>;
};
export default ProductDetailsLayout;